/**
 * CLARTEXT · Fuehrerscheinkontrolle · Block D — Mitarbeiter-App-Seite.
 *
 * Zeigt die Fuehrerschein-Aufgabe fuer den eingeloggten Mitarbeiter:
 *   - Piktogramme der zugewiesenen Fahrzeugkategorien (Block-C-Bilder,
 *     Wiederverwendung `vehicleCategoryIcons.js`).
 *   - Platzhaltertext aus `services/fuehrerschein/declaration_texts.py`
 *     inkl. sichtbarem Banner "PLATZHALTER — anwaltlich noch nicht
 *     geprueft" (Betreiberantwort 7b).
 *   - Foto-Upload-Widget (Kamera/Dateiwahl auf Mobil, Datei auf Desktop).
 *   - Aktive Bestaetigungs-Checkbox.
 *   - Submit-Button, disabled solange Foto ODER Checkbox fehlt.
 *
 * Nach erfolgreicher Einreichung: Wechsel in einen Wartezustand
 * "wartet auf Pruefung" auf derselben Seite. Kein Zurueckwechsel zur
 * Anlage-Form solange OFFEN.
 *
 * Zero-Interpretation: KEINE Klassenanzeige (BE-002), KEINE
 * FeV-/StVZO-Ableitung. Reine Foto-Einreichung + Erklaerung.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useEmployeeSession } from "@/hooks/useEmployeeSession";
import { useMonteurT } from "@/i18n/monteur";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ChevronLeft,
  Car,
  AlertTriangle,
  Camera,
  Check,
  CheckCircle2,
  Clock,
  Upload,
  Info,
} from "lucide-react";
import { getVehiclePictogramSrc } from "@/lib/vehicleCategoryIcons";
import {
  assessImageQuality,
  IMAGE_QUALITY_BLURRY,
} from "@/lib/imageQuality";

const MAX_PHOTO_BYTES = 12 * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];

export default function MonteurFuehrerschein() {
  const navigate = useNavigate();
  const { session } = useEmployeeSession();
  const t = useMonteurT();

  const [task, setTask] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  // Vorderseite (Bestand aus Block D).
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Rueckseite (Nachtrag Feb 2026 · zweiseitiger Foto-Upload). Beide Seiten
  // sind fuer neue Einreichungen Pflicht (Frage 4a).
  const [fileBack, setFileBack] = useState(null);
  const [previewUrlBack, setPreviewUrlBack] = useState(null);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // CLARTEXT · Block E — Client-seitige Bildqualitaets-Heuristik (nicht
  // blockierend). `qualityNote` zeigt eine dezente Warnung neben dem
  // Foto-Preview an, wenn die Laplace-Varianz als deutlich unscharf gilt.
  const [qualityNote, setQualityNote] = useState(null);
  const [qualityNoteBack, setQualityNoteBack] = useState(null);
  // CLARTEXT · Block E — OCR-Lesehilfe (Read + Polling). Wird erst im
  // `submitted`-Zustand angefordert; bei `processing` pollt der Effekt weiter.
  const [ocrState, setOcrState] = useState({ status: "idle", reading: null });
  const fileInputRef = useRef(null);
  const fileInputBackRef = useRef(null);

  // --- Initial load -----------------------------------------------------
  useEffect(() => {
    // AUTH-SICHERHEIT V2 · Phase 2: Session-Marker ist employee.id.
    if (!session?.employee?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [taskData, catData] = await Promise.all([
          api.getMyDriverTask(),
          api.getMyVehicleCategories(),
        ]);
        if (cancelled) return;
        setTask(taskData);
        setCategories(Array.isArray(catData) ? catData : []);
      } catch (e) {
        if (!cancelled) toast.error(t("fuehrerschein.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // --- Preview-URL cleanup ---------------------------------------------
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!fileBack) {
      setPreviewUrlBack(null);
      return;
    }
    const url = URL.createObjectURL(fileBack);
    setPreviewUrlBack(url);
    return () => URL.revokeObjectURL(url);
  }, [fileBack]);

  // --- Block E: OCR-Lesehilfe Read + Polling ---------------------------
  // Startet erst wenn der Record submitted ist. Solange `processing` gemeldet
  // wird, pollt der Effekt alle 3 s. Bei `ready` oder `unavailable` stoppt er.
  // Fail-open: Netzwerkfehler werden geloggt, aber nicht als UI-Fehler
  // signalisiert (der Foto-Submit bleibt gueltig, siehe BE-004).
  useEffect(() => {
    if (task?.status !== "submitted") return;
    let cancelled = false;
    let attempt = 0;
    const MAX_ATTEMPTS = 20; // ~60 s

    const tick = async () => {
      try {
        const res = await api.getMyDriverOcr();
        if (cancelled) return;
        setOcrState({ status: res?.status || "processing", reading: res?.reading || null });
        if (res?.status === "ready" || res?.status === "unavailable" || res?.status === "no_task") {
          return;
        }
      } catch (_e) {
        if (cancelled) return;
      }
      attempt += 1;
      if (attempt >= MAX_ATTEMPTS) {
        if (!cancelled) {
          setOcrState((prev) => (
            prev.status === "ready" || prev.status === "unavailable"
              ? prev
              : { status: "unavailable", reading: null }
          ));
        }
        return;
      }
      setTimeout(tick, 3000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [task?.status]);

  const assignedCategoryDetails = useMemo(() => {
    if (!task) return [];
    const ids = new Set(task.assigned_categories || []);
    return categories.filter((c) => ids.has(c.identifier));
  }, [task, categories]);

  const canSubmit =
    task?.status === "open" &&
    file !== null &&
    fileBack !== null &&
    declarationAccepted &&
    !submitting;

  // --- File handling ----------------------------------------------------
  const _validateAndAssess = async (f, setStateFile, setStateNote) => {
    if (!ALLOWED_MIME_TYPES.includes((f.type || "").toLowerCase())) {
      toast.error(t("fuehrerschein.errFormat"));
      return;
    }
    if (f.size > MAX_PHOTO_BYTES) {
      toast.error(t("fuehrerschein.errSize"));
      return;
    }
    setStateFile(f);
    // Block E · P1-Heuristik (nicht blockierend). Fail-open bei jedem
    // Auswertungsfehler — der Mitarbeiter kann in jedem Fall einreichen.
    setStateNote(null);
    try {
      const result = await assessImageQuality(f);
      if (result?.quality === IMAGE_QUALITY_BLURRY) {
        setStateNote(result.note);
        toast.warning(result.note);
      }
    } catch (_e) {
      // Neutral — keine Fehlermeldung, keine Blockade.
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await _validateAndAssess(f, setFile, setQualityNote);
  };

  const handleFileBackChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await _validateAndAssess(f, setFileBack, setQualityNoteBack);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("declaration_accepted", "true");
      fd.append("declaration_text_version", task.declaration.text_version);
      fd.append("photo", file, file.name || "fuehrerschein-vorderseite.jpg");
      fd.append(
        "photo_back",
        fileBack,
        fileBack.name || "fuehrerschein-rueckseite.jpg"
      );
      const res = await api.submitMyDriverTask(fd);
      toast.success(t("fuehrerschein.submitSuccess"));
      setTask({
        status: "submitted",
        assigned_categories: res.assigned_categories,
        declaration: task.declaration,
        submitted_at: res.submitted_at,
      });
      setFile(null);
      setFileBack(null);
      setDeclarationAccepted(false);
      setQualityNote(null);
      setQualityNoteBack(null);
      setOcrState({ status: "processing", reading: null });
    } catch (e) {
      // i18n (Juni 2026): kein roher Backend-`detail`-Text mehr in der UI —
      // immer die übersetzte, nutzerfreundliche Meldung.
      toast.error(t("fuehrerschein.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  // --- Rendering --------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }

  if (task?.status === "not_applicable") {
    return (
      <div
        className="min-h-screen bg-slate-50"
        data-testid="monteur-fuehrerschein-not-applicable"
      >
        <PageHeader onBack={() => navigate("/monteur")} t={t} />
        <div className="max-w-lg mx-auto px-5 py-6">
          <Card className="p-6 text-center">
            <Info className="w-8 h-8 text-slate-500 mx-auto mb-2" />
            <p className="text-sm text-slate-600">
              {t("fuehrerschein.notApplicable")}
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="monteur-fuehrerschein-page">
      <PageHeader onBack={() => navigate("/monteur")} t={t} />

      <div className="max-w-lg mx-auto px-5 py-6 space-y-5">
        {/* Kategorien-Anzeige */}
        <section data-testid="monteur-fuehrerschein-categories-section">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">
            {t("fuehrerschein.categoriesTitle")}
          </h2>
          <p className="text-sm text-slate-500 mb-3">
            {t("fuehrerschein.categoriesSubtitle")}
          </p>
          {assignedCategoryDetails.length === 0 ? (
            <Card className="p-4 text-sm text-slate-500">
              {t("fuehrerschein.categoriesEmpty")}
            </Card>
          ) : (
            <div
              className="grid grid-cols-2 gap-2"
              data-testid="monteur-fuehrerschein-category-grid"
            >
              {assignedCategoryDetails.map((cat) => {
                const src = getVehiclePictogramSrc(cat.pictogram_key);
                return (
                  <div
                    key={cat.identifier}
                    className="border border-slate-200 rounded-md p-2 flex flex-col items-center gap-2 bg-white"
                    data-testid={`monteur-fuehrerschein-category-${cat.identifier}`}
                  >
                    <div className="w-full aspect-square bg-[#F0F0F0] rounded flex items-center justify-center overflow-hidden">
                      {src ? (
                        <img
                          src={src}
                          alt={cat.pictogram_alt_text || cat.display_name}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <Car className="w-8 h-8 text-slate-400" />
                      )}
                    </div>
                    <div className="text-xs font-medium text-slate-800 text-center leading-tight">
                      {cat.display_name}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Block G (Feb 2026 · §11.g/§4.6): up_to_date-Panel — explizit
            statt eines leeren "not_applicable"-Zustands. Zeigt dem Fahrer,
            wann seine naechste Fuehrerschein-Pruefung ansteht.

            UX-Verfeinerung (2026-02-09): Das Panel soll DEZENT sein — keine
            grelle Erfolgs-Anzeige. Weiche slate-neutrale Farbe, kleine
            Zeile fuer das Datum. Der Fahrer soll wissen, warum nichts zu
            tun ist, aber nicht gefeiert werden. */}
        {task?.status === "up_to_date" && (
          <Card
            className="p-4 border-slate-200 bg-white"
            data-testid="monteur-fuehrerschein-up-to-date-card"
          >
            <div className="flex items-start gap-3">
              <Check
                className="w-5 h-5 text-slate-400 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">
                  {t("fuehrerschein.upToDateTitle")}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {t("fuehrerschein.upToDateText")}
                </p>
                {task?.next_due_at ? (
                  <p
                    className="text-xs text-slate-500 mt-2"
                    data-testid="monteur-fuehrerschein-next-due-at"
                  >
                    {t("fuehrerschein.nextDue")}{" "}
                    <span className="font-medium text-slate-700">
                      {new Date(task.next_due_at).toLocaleDateString("de-DE")}
                    </span>
                  </p>
                ) : null}
                {task?.last_decision_at ? (
                  <p className="text-xs text-slate-400 mt-0.5">
                    {t("fuehrerschein.lastDecision")}{" "}
                    {new Date(task.last_decision_at).toLocaleDateString("de-DE")}
                  </p>
                ) : null}
              </div>
            </div>
          </Card>
        )}

        {/* Submitted-Zustand */}
        {task?.status === "submitted" && (
          <>
            <Card
              className="p-5 border-emerald-200 bg-emerald-50"
              data-testid="monteur-fuehrerschein-submitted-card"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-emerald-900">
                    {t("fuehrerschein.submittedTitle")}
                  </div>
                  <div className="text-sm text-emerald-800 mt-1">
                    {t("fuehrerschein.submittedText")}
                  </div>
                  {task.submitted_at && (
                    <div className="flex items-center gap-1 text-xs text-emerald-700 mt-2">
                      <Clock className="w-3 h-3" />
                      {t("fuehrerschein.submittedAt")}{" "}
                      {new Date(task.submitted_at).toLocaleString("de-DE")}
                    </div>
                  )}
                </div>
              </div>
            </Card>

            <OcrReadingCard state={ocrState} t={t} />
          </>
        )}

        {/* Open-Zustand: Erklärung + Upload + Submit */}
        {task?.status === "open" && (
          <>
            {/* Erklärung (BE-002 · anwaltlich geprüft, kein Platzhalter-Banner mehr) */}
            <section data-testid="monteur-fuehrerschein-declaration-section">
              <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">
                {t("fuehrerschein.declarationTitle")}
              </h2>

              <div
                className="rounded-md bg-white border border-slate-200 p-4 text-sm text-slate-800 whitespace-pre-line"
                data-testid="monteur-fuehrerschein-declaration-body"
              >
                {task.declaration?.body}
              </div>

              <label
                htmlFor="monteur-fs-accept"
                className="mt-3 flex items-start gap-2 cursor-pointer select-none"
              >
                <Checkbox
                  id="monteur-fs-accept"
                  checked={declarationAccepted}
                  onCheckedChange={(v) => setDeclarationAccepted(v === true)}
                  data-testid="monteur-fuehrerschein-accept-checkbox"
                />
                <span className="text-sm text-slate-800 leading-snug">
                  {t("fuehrerschein.declarationAccept")}
                </span>
              </label>
            </section>

            {/* Foto-Upload */}
            <section data-testid="monteur-fuehrerschein-photo-section">
              <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">
                {t("fuehrerschein.photoFrontTitle")}
              </h2>
              <p className="text-sm text-slate-500 mb-3">
                {t("fuehrerschein.photoFrontHint")}
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                capture="environment"
                onChange={handleFileChange}
                className="hidden"
                data-testid="monteur-fuehrerschein-file-input"
              />

              {previewUrl ? (
                <div className="space-y-2">
                  <div className="rounded-md overflow-hidden border border-slate-200 bg-slate-100">
                    <img
                      src={previewUrl}
                      alt={t("fuehrerschein.previewFrontAlt")}
                      className="w-full max-h-72 object-contain"
                      data-testid="monteur-fuehrerschein-photo-preview"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="monteur-fuehrerschein-photo-replace"
                    >
                      <Camera className="w-4 h-4 mr-2" /> {t("fuehrerschein.otherPhoto")}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => { setFile(null); setQualityNote(null); }}
                      data-testid="monteur-fuehrerschein-photo-clear"
                    >
                      {t("fuehrerschein.remove")}
                    </Button>
                  </div>
                  {qualityNote && (
                    <div
                      className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2"
                      data-testid="monteur-fuehrerschein-quality-warning"
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                      <div className="text-xs text-amber-900 leading-snug">
                        {qualityNote}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-300 rounded-md p-8 flex flex-col items-center gap-2 bg-white hover:border-slate-500"
                  data-testid="monteur-fuehrerschein-photo-picker"
                >
                  <Upload className="w-6 h-6 text-slate-500" />
                  <span className="text-sm text-slate-700">
                    {t("fuehrerschein.pickFront")}
                  </span>
                  <span className="text-xs text-slate-500">
                    {t("fuehrerschein.pickLimits")}
                  </span>
                </button>
              )}
            </section>

            {/* Foto-Upload · Rueckseite (Nachtrag Feb 2026, Frage 4a Pflicht) */}
            <section data-testid="monteur-fuehrerschein-photo-back-section">
              <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">
                {t("fuehrerschein.photoBackTitle")}
              </h2>
              <p className="text-sm text-slate-500 mb-3">
                {t("fuehrerschein.photoBackHint")}
              </p>

              <input
                ref={fileInputBackRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                capture="environment"
                onChange={handleFileBackChange}
                className="hidden"
                data-testid="monteur-fuehrerschein-file-input-back"
              />

              {previewUrlBack ? (
                <div className="space-y-2">
                  <div className="rounded-md overflow-hidden border border-slate-200 bg-slate-100">
                    <img
                      src={previewUrlBack}
                      alt={t("fuehrerschein.previewBackAlt")}
                      className="w-full max-h-72 object-contain"
                      data-testid="monteur-fuehrerschein-photo-preview-back"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => fileInputBackRef.current?.click()}
                      data-testid="monteur-fuehrerschein-photo-replace-back"
                    >
                      <Camera className="w-4 h-4 mr-2" /> {t("fuehrerschein.otherPhoto")}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => { setFileBack(null); setQualityNoteBack(null); }}
                      data-testid="monteur-fuehrerschein-photo-clear-back"
                    >
                      {t("fuehrerschein.remove")}
                    </Button>
                  </div>
                  {qualityNoteBack && (
                    <div
                      className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2"
                      data-testid="monteur-fuehrerschein-quality-warning-back"
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                      <div className="text-xs text-amber-900 leading-snug">
                        {qualityNoteBack}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputBackRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-300 rounded-md p-8 flex flex-col items-center gap-2 bg-white hover:border-slate-500"
                  data-testid="monteur-fuehrerschein-photo-picker-back"
                >
                  <Upload className="w-6 h-6 text-slate-500" />
                  <span className="text-sm text-slate-700">
                    {t("fuehrerschein.pickBack")}
                  </span>
                  <span className="text-xs text-slate-500">
                    {t("fuehrerschein.pickLimits")}
                  </span>
                </button>
              )}
            </section>

            {/* Submit */}
            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={!canSubmit}
              data-testid="monteur-fuehrerschein-submit"
            >
              {submitting ? t("fuehrerschein.submitting") : t("fuehrerschein.submit")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function PageHeader({ onBack, t }) {
  return (
    <header
      className="bg-white border-b border-slate-200"
      data-testid="monteur-fuehrerschein-header"
    >
      <div className="max-w-lg mx-auto px-5 py-4 flex items-center gap-2">
        <button
          onClick={onBack}
          className="p-2 -ml-2 text-slate-500 hover:text-slate-900"
          aria-label={t("common.back")}
          data-testid="monteur-fuehrerschein-back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="font-heading text-lg font-bold text-slate-900">
          {t("fuehrerschein.title")}
        </div>
      </div>
    </header>
  );
}

/**
 * CLARTEXT · Block E — OCR-Anzeige-Karte fuer den Mitarbeiter.
 *
 * Drei Zustaende:
 *   - processing:  neutraler Loader-Hinweis
 *   - unavailable: expliziter Fehler-Banner "OCR temporaer nicht verfuegbar…"
 *   - ready:       Ergebnis-Anzeige MIT Banner "Automatische Abschrift — keine
 *                  Pruefung durch CLARTEXT, ersetzt keine Sichtpruefung."
 *
 * Betreiberantwort Frage 7b: Banner-Wortlaut deckungsgleich.
 * Betreiberantwort Frage 4b: Fehler ist sichtbar (kein stilles Verschlucken).
 */
function OcrReadingCard({ state, t }) {
  const status = state?.status || "processing";
  const reading = state?.reading || null;

  if (status === "processing") {
    return (
      <Card
        className="p-4 border-slate-200 bg-white"
        data-testid="monteur-fuehrerschein-ocr-processing"
      >
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-slate-500 shrink-0" />
          <div className="text-sm text-slate-700">
            {t("fuehrerschein.ocrPreparing")}
          </div>
        </div>
      </Card>
    );
  }

  if (status === "unavailable") {
    // Betreiber-Entscheidung 2a (Juni 2026): ein etwaiger Backend-Hinweis
    // (source_notes) behält Vorrang; nur der Standard-Fallback ist übersetzt.
    const note = reading?.source_notes?.[0]
      || t("fuehrerschein.ocrUnavailable");
    return (
      <Card
        className="p-4 border-amber-200 bg-amber-50"
        data-testid="monteur-fuehrerschein-ocr-unavailable"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-snug">
            {note}
          </div>
        </div>
      </Card>
    );
  }

  if (status === "ready" && reading) {
    const classes = Array.isArray(reading.licence_classes)
      ? reading.licence_classes
      : [];
    return (
      <Card
        className="p-4 border-slate-200 bg-white"
        data-testid="monteur-fuehrerschein-ocr-ready"
      >
        <div
          className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 mb-3"
          data-testid="monteur-fuehrerschein-ocr-banner"
        >
          <Info className="w-4 h-4 text-slate-700 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-800 leading-snug">
            <strong>{t("fuehrerschein.ocrBannerStrong")}</strong>
            {t("fuehrerschein.ocrBannerRest")}
          </div>
        </div>
        <dl className="grid grid-cols-1 gap-2 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              {t("fuehrerschein.ocrName")}
            </dt>
            <dd
              className="text-slate-900"
              data-testid="monteur-fuehrerschein-ocr-full-name"
            >
              {reading.full_name || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              {t("fuehrerschein.ocrExpiry")}
            </dt>
            <dd
              className="text-slate-900"
              data-testid="monteur-fuehrerschein-ocr-expiry-date"
            >
              {reading.expiry_date || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">
              {t("fuehrerschein.ocrClasses")}
            </dt>
            <dd
              className="text-slate-900"
              data-testid="monteur-fuehrerschein-ocr-classes"
            >
              {classes.length ? classes.join(", ") : "—"}
            </dd>
          </div>
        </dl>
      </Card>
    );
  }

  return null;
}
